import axios from 'axios';

/**
 * Refresh utility for lsgo-authentication-issued access tokens.
 *
 * lsgo-platform-web is a public OAuth client, so the refresh token itself
 * is never handed to JS — lsgo-authentication delivers it as an httpOnly,
 * SameSite=Strict cookie scoped to /token (see lsgo-authentication's
 * src/routes/token.js: deliverRefreshToken). The browser sends it back on
 * its own; the caller only needs client_id in the body. This means every
 * request to /token, including the initial code exchange, MUST be made
 * with withCredentials: true — otherwise the browser never stores the
 * cookie in the first place (cross-origin responses without a credentialed
 * request have their Set-Cookie silently dropped).
 *
 * Standalone in lsgo-teacher for now (not lsgo-common yet) — no React
 * dependency, so it can be lifted into the shared package later without
 * change once other consuming apps need the same behaviour.
 */

/**
 * Exchanges the current refresh-token cookie for a new access token.
 * Rejects with the /token error body (e.g. { error: 'invalid_grant' }) if
 * the refresh token is missing, expired, or already rotated.
 */
export async function refreshLsgoAuthenticationToken({ authBaseUrl, clientId }) {
    const res = await axios.post(
        `${authBaseUrl}/token`,
        new URLSearchParams({ grant_type: 'refresh_token', client_id: clientId }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, withCredentials: true }
    );
    return res.data; // { access_token, token_type, expires_in }
}

/**
 * Schedules a single refresh call `leadSeconds` before the current access
 * token's `expiresInSeconds` runs out, rather than waiting for it to
 * actually expire. Returns a cancel function — call it whenever the token
 * changes or the component unmounts, so timers never stack up.
 */
export function scheduleLsgoAuthenticationRefresh({ expiresInSeconds, leadSeconds = 15, onDue }) {
    const delayMs = Math.max((expiresInSeconds - leadSeconds) * 1000, 1000);
    const timerId = setTimeout(onDue, delayMs);
    return () => clearTimeout(timerId);
}

/**
 * Signs out of the current lsgo-authentication session: RFC 7009-style
 * revocation of the refresh-token cookie (src/routes/token.js's
 * POST /token/revoke). This isn't just "stop refreshing" — revoking the
 * refresh token also blocklists its whole token family in Redis, so any
 * access token already issued from this session is rejected immediately
 * too, not just future refresh attempts.
 */
export async function signOutLsgoAuthentication({ authBaseUrl }) {
    await axios.post(`${authBaseUrl}/token/revoke`, null, { withCredentials: true });
}
